import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";
import { getUserData } from "./const";
import { getSessionBranchId } from "../utils/sessionBranch";
import {
  createBankAccount,
  listBankAccounts,
  updateBankAccountStatus,
} from "../erpClient";
import Pagination from "../components/Pagination";
import { useNavigate } from "react-router-dom";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

import { createErpSocket } from "../utils/socketIo";
const ITEMS_PER_PAGE = 10;

/** API returns MONEY / METAL; form selects use money / metal */
function normalizeAccountTypeToUi(value) {
  const t = String(value ?? "")
    .toLowerCase()
    .trim();
  if (t === "metal") return "metal";
  if (t === "money") return "money";
  return "";
}

function accountTypeToApi(uiValue) {
  const t = String(uiValue ?? "")
    .toLowerCase()
    .trim();
  if (t === "metal") return "METAL";
  if (t === "money") return "MONEY";
  return String(uiValue ?? "").toUpperCase();
}

function formatAccountTypeLabel(uiValue) {
  const t = String(uiValue ?? "")
    .toLowerCase()
    .trim();
  if (t === "metal") return "Metal";
  if (t === "money") return "Money";
  return uiValue || "";
}

function isMetalAccountType(accountType) {
  return String(accountType ?? "")
    .toLowerCase()
    .trim() === "metal";
}

/** Dashboard uses current_balance; legacy list used available_balance. */
function pickBankBalanceRaw(bank) {
  const candidates = [
    bank?.current_balance,
    bank?.available_balance,
    bank?.balance,
    bank?.opening_balance,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    const n = parseFloat(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function formatBankBalance(accountType, amount) {
  const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  if (isMetalAccountType(accountType)) {
    return `${safe.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    })} gm`;
  }
  return `₹${safe.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function mapBankBalance(bank) {
  return formatBankBalance(
    bank?.account_type,
    pickBankBalanceRaw(bank),
  );
}

function BankDetailsTable() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Add Bank form states
  const [bankName, setBankName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [accountStatus, setAccountStatus] = useState("active");
  const [socket, setSocket] = useState(null);

  const userData = getUserData();

  const fetchBankDetails = async () => {
    setIsLoading(true);
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: normalizeAccountTypeToUi(bank.account_type),
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance: mapBankBalance(bank),
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        toast("No bank accounts found");
      } else if (response && response.success === false) {
        console.error("Server returned error:", response.message);
        setBankAccounts([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        console.error("Unexpected data format:", response);
        setBankAccounts([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateBankAccount = async (updatedAccount) => {
    const useForInvoice = updatedAccount.useForInvoice ? 1 : 0;
    const useForPOS = updatedAccount.useForPOS ? 1 : 0;

    try {
      const response = await updateBankAccountStatus({
        id: updatedAccount.id,
        branch_id: getSessionBranchId(),
        bank_name: updatedAccount.bankName,
        holder_name: updatedAccount.accountHolder,
        account_type: accountTypeToApi(updatedAccount.accountType),
        ifsc_code: updatedAccount.ifscCode || "",
        account_number: updatedAccount.accountNumber || "",
        opening_balance: Number(updatedAccount.openingBalance) || 0,
        use_for_invoice: useForInvoice,
        use_for_pos: useForPOS,
        status: updatedAccount.status === "Active" ? 1 : 0,
      });

      if (response?.success) {
        toast.success("Bank account updated successfully");
        // No list refresh needed; we already updated local state in handleSaveChanges
        // and socket events will keep other clients in sync.
      }
    } catch (error) {
      console.error("Error updating bank account details:", error);
      toast.error(
        error.response?.data?.message || "Failed to update bank account",
      );
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in BankDetailsTable, refetching bank details...",
        event.detail,
      );
      setCurrentPage(1);
      fetchBankDetails();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("BankDetailsTable: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("BankDetailsTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("BankDetailsTable: Socket connection error:", error);
    });

    newSocket.on("bank_account_updated", (data) => {
      if (!data?.bank_account) return;
      const ba = data.bank_account;

      // Force refresh to ensure badges are synchronized across all devices
      // console.log("Bank account updated via socket:", data.bank_account);

      setBankAccounts((prev) =>
        prev.map((account) =>
          account.id === ba.id
            ? {
              ...account,
              bankName: ba.bank_name || account.bankName,
              accountHolder: ba.holder_name || account.accountHolder,
              accountType: normalizeAccountTypeToUi(
                ba.account_type ?? account.accountType,
              ),
              ifscCode: ba.ifsc_code || ba.IFSC_code || account.ifscCode,
              accountNumber: ba.account_number || account.accountNumber,
              openingBalance: ba.opening_balance ?? account.openingBalance,
              balance: (() => {
                const merged = { ...account, ...ba };
                const raw = pickBankBalanceRaw(merged);
                if (raw !== 0 || merged.current_balance != null || merged.available_balance != null || merged.balance != null) {
                  return mapBankBalance(merged);
                }
                return account.balance ?? mapBankBalance(merged);
              })(),
              status:
                ba.status === "1" || ba.status === 1 ? "Active" : "Inactive",
              statusClass:
                ba.status === "1" || ba.status === 1
                  ? "bg-success-subtle text-success"
                  : "bg-danger-subtle text-danger",
              // Invoice/POS flags (new ERP uses use_for_invoice/use_for_pos)
              useForInvoice:
                ba.use_for_invoice === 1 ||
                ba.use_for_invoice === "1" ||
                ba.invoice === 1 ||
                ba.invoice === "1" ||
                account.useForInvoice === true,
              useForPOS:
                ba.use_for_pos === 1 ||
                ba.use_for_pos === "1" ||
                ba.pos === 1 ||
                ba.pos === "1" ||
                account.useForPOS === true,
            }
            : account,
        ),
      );

      // Optional: Force a complete refresh if needed for critical updates
      // Uncomment the line below if badge synchronization issues persist
      // fetchBankDetails();
    });

    newSocket.on("bank_account_created", (data) => {
      if (!data?.bank_account) return;
      const ba = data.bank_account;

      const newAccount = {
        id: ba.id,
        bankName: ba.bank_name || "",
        accountHolder: ba.holder_name || "",
        accountType: normalizeAccountTypeToUi(ba.account_type),
        ifscCode: ba.ifsc_code || ba.IFSC_code || "",
        accountNumber: ba.account_number || "",
        openingBalance: ba.opening_balance ?? "",
        balance: mapBankBalance(ba),
        status: ba.status === "1" || ba.status === 1 ? "Active" : "Inactive",
        statusClass:
          ba.status === "1" || ba.status === 1
            ? "bg-success-subtle text-success"
            : "bg-danger-subtle text-danger",
        useForInvoice:
          ba.use_for_invoice === 1 ||
          ba.use_for_invoice === "1" ||
          ba.invoice === 1 ||
          ba.invoice === "1",
        useForPOS:
          ba.use_for_pos === 1 ||
          ba.use_for_pos === "1" ||
          ba.pos === 1 ||
          ba.pos === "1",
      };

      setBankAccounts((prev) => {
        // Check if account already exists (avoid duplicates)
        const exists = prev.some((account) => account.id === newAccount.id);
        if (exists) {
          return prev.map((account) =>
            account.id === newAccount.id ? newAccount : account,
          );
        }
        // Add new account at the beginning (newest first)
        return [newAccount, ...prev].sort((a, b) => b.id - a.id);
      });
    });

    return () => {
      newSocket.off("bank_account_updated");
      newSocket.off("bank_account_created");
      newSocket.disconnect();
    };
  }, []);

  const handleEditClick = (account) => {
    setSelectedAccount({
      ...account,
      accountType: normalizeAccountTypeToUi(account.accountType),
    });
    setShowEditModal(true);
  };

  const handleCheckboxChange = (field) => {
    setSelectedAccount((prev) => {
      const updatedValue = !prev[field];

      return {
        ...prev,
        [field]: updatedValue,
      };
    });
  };

  const handleSaveChanges = () => {
    // console.log("Saving changes with account:", selectedAccount);

    // Create a new update request to ensure proper handling
    const updatedData = {
      ...selectedAccount,
      useForInvoice: !!selectedAccount.useForInvoice, // Ensure boolean
      useForPOS: !!selectedAccount.useForPOS, // Ensure boolean
    };

    // console.log("Selected account before update:", updatedData);

    // Update local state immediately to reflect changes in UI
    setBankAccounts((prevAccounts) =>
      prevAccounts.map((account) => {
        if (account.id === selectedAccount.id) {
          return {
            ...selectedAccount,
            statusClass:
              selectedAccount.status === "Active"
                ? "bg-success-subtle text-success"
                : "bg-danger-subtle text-danger",
          };
        } else {
          // For invoice/POS flags, only uncheck others if this account is checked
          return {
            ...account,
            useForInvoice: selectedAccount.useForInvoice
              ? false
              : account.useForInvoice,
            useForPOS: selectedAccount.useForPOS ? false : account.useForPOS,
          };
        }
      }),
    );

    // Send update to API
    updateBankAccount(updatedData);
    setShowEditModal(false);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await createBankAccount({
        branch_id: getSessionBranchId(),
        bank_name: bankName,
        holder_name: accountHolderName,
        account_type: accountTypeToApi(accountType),
        ifsc_code: ifscCode,
        account_number: accountNumber,
        opening_balance: Number(openingBalance) || 0,
      });

      if (response?.success) {
        toast.success(response.message || "Bank account added successfully");
        // Update list from API response (or let socket event insert it)
        const data = response.data?.bank_account || response.data || {};
        const nextId = data.id;
        if (nextId !== undefined && nextId !== null) {
          const next = {
            id: nextId,
            bankName: data.bank_name ?? bankName ?? "",
            accountHolder: data.holder_name ?? accountHolderName ?? "",
            accountType: normalizeAccountTypeToUi(
              data.account_type ?? accountType,
            ),
            ifscCode: data.ifsc_code ?? ifscCode ?? "",
            accountNumber: data.account_number ?? accountNumber ?? "",
            openingBalance:
              data.opening_balance !== undefined &&
                data.opening_balance !== null
                ? data.opening_balance
                : Number(openingBalance) || 0,
            balance: mapBankBalance({
              account_type: data.account_type ?? accountType,
              current_balance: data.current_balance,
              available_balance: data.available_balance,
              balance: data.balance,
              opening_balance:
                data.opening_balance !== undefined &&
                  data.opening_balance !== null
                  ? data.opening_balance
                  : Number(openingBalance) || 0,
            }),
            status: (data.status ?? 1) === 1 ? "Active" : "Inactive",
            statusClass:
              (data.status ?? 1) === 1
                ? "bg-success-subtle text-success"
                : "bg-danger-subtle text-danger",
            useForInvoice: (data.use_for_invoice ?? 0) === 1,
            useForPOS: (data.use_for_pos ?? 0) === 1,
          };
          setBankAccounts((prev) => {
            const exists = prev.some((a) => a.id === next.id);
            const merged = exists
              ? prev.map((a) => (a.id === next.id ? { ...a, ...next } : a))
              : [next, ...prev];
            return merged.sort((a, b) => b.id - a.id);
          });
        } else {
          // If backend doesn't return id, do a list refresh as fallback.
          fetchBankDetails();
        }
      }

      setShowAddModal(false);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);

      // Reset form fields
      setBankName("");
      setAccountHolderName("");
      setAccountType("");
      setIfscCode("");
      setAccountNumber("");
      setOpeningBalance("");
      setAccountStatus("active");

      // Refresh bank list
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message || "Failed to add bank account",
      );
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const filteredAccounts = bankAccounts.filter((account) => {
    const matchesSearchQuery =
      account.bankName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.accountHolder.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTypeFilter = typeFilter
      ? account.accountType === typeFilter
      : true;
    const matchesStatusFilter = statusFilter
      ? account.status === statusFilter
      : true;

    return matchesSearchQuery && matchesTypeFilter && matchesStatusFilter;
  });

  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const displayedAccounts = filteredAccounts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
  };

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <div>
              <h4 className="card-title mb-0">Bank Table</h4>
            </div>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-info ${buttonClass}`}
                onClick={() => navigate("/bankledger")}
              >
                Ledger
              </button>
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => setShowAddModal(true)}
              >
                Add Bank
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <div className="d-flex gap-3">
                <select
                  className={`form-select ${buttonTableStyle === "square" ? "rounded-0" : ""
                    }`}
                  style={{ maxWidth: "200px" }}
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="money">Money</option>

                  <option value="metal">Metal</option>
                </select>
                <select
                  className={`form-select ${buttonTableStyle === "square" ? "rounded-0" : ""
                    }`}
                  style={{ maxWidth: "200px" }}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div
              className={`table-responsive mb-4 border ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
            >
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Bank</th>
                    <th>Holder</th>
                    <th>Type</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    displayedAccounts.map((account) => (
                      <tr key={account.id}>
                        <td>
                          {account.bankName}
                          {account.useForInvoice && (
                            <span className="badge bg-primary-subtle text-primary ms-2">
                              Invoice
                            </span>
                          )}
                          {account.useForPOS && (
                            <span className="badge bg-warning-subtle text-warning ms-2">
                              POS
                            </span>
                          )}
                        </td>
                        <td>{account.accountHolder}</td>
                        <td>{formatAccountTypeLabel(account.accountType)}</td>
                        <td>{account.balance}</td>
                        <td>
                          <span className={`badge ${account.statusClass}`}>
                            {account.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`btn btn-primary btn-sm me-2 ${buttonClass}`}
                            onClick={() => handleEditClick(account)}
                          >
                            <Icon
                              icon="solar:pen-bold"
                              width="16"
                              height="16"
                            />
                          </button>
                          {/* <button className="btn btn-secondary btn-sm">
                          <Icon
                            icon="solar:bill-list-bold"
                            width="16"
                            height="16"
                          />
                        </button> */}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredAccounts.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Add Bank Account Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Bank Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Bank Name"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className={buttonClass}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Account Holder Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Account Holder Name"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  className={buttonClass}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Dealing Type <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                >
                  <option value="">Select Dealing Type</option>
                  <option value="money">Money</option>

                  <option value="metal">Metal</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>IFSC Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter IFSC Code"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Account Number"
                  value={accountNumber}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setAccountNumber(numericValue);
                  }}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Opening Balance</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Opening Balance"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Col>
            </Row>

            {/* <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  Account Status 
                </Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Active"
                    name="accountStatus"
                    value="active"
                    checked={accountStatus === "active"}
                    onChange={(e) => setAccountStatus(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    label="Inactive"
                    name="accountStatus"
                    value="inactive"
                    checked={accountStatus === "inactive"}
                    onChange={(e) => setAccountStatus(e.target.value)}
                  />
                </div>
              </Col>
            </Row> */}

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <Button type="submit" variant="primary" className={buttonClass}>
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Success Modal */}

      {/* Edit Bank Account Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAccount && (
            <Form>
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.bankName}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        bankName: e.target.value,
                      })
                    }
                    className={buttonClass}
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Account Holder Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.accountHolder}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        accountHolder: e.target.value,
                      })
                    }
                    className={buttonClass}
                  />
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Dealing Type</Form.Label>
                  <Form.Select
                    value={selectedAccount.accountType}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        accountType: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  >
                    <option value="money">Money</option>

                    <option value="metal">Metal</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>IFSC Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.ifscCode || ""}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        ifscCode: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  />
                </Form.Group>
              </div>

              <div className="row">
                {/* Account Number */}
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.accountNumber || ""}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        accountNumber: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  />
                </Form.Group>

                {/* Opening Balance */}
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Opening Balance</Form.Label>
                  <Form.Control
                    type="number"
                    value={selectedAccount.openingBalance || ""}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        openingBalance: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  />
                </Form.Group>
              </div>

              {/* Available Balance (Read-Only) */}
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Available Balance</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedAccount.balance}
                  readOnly
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Form.Group>

              {/* Account Status */}
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Account Status</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Active"
                    name="accountStatus"
                    value="Active"
                    checked={selectedAccount.status === "Active"}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        status: e.target.value,
                      })
                    }
                  />
                  <Form.Check
                    type="radio"
                    label="Inactive"
                    name="accountStatus"
                    value="Inactive"
                    checked={selectedAccount.status === "Inactive"}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        status: e.target.value,
                      })
                    }
                  />
                </div>
              </Form.Group>

              {/* Payment Preferences */}
              <Form.Group className="mb-3">
                <Form.Label>Payment Preferences</Form.Label>
                <div className="d-flex">
                  <Form.Check
                    type="checkbox"
                    label="Use for Invoice"
                    checked={selectedAccount.useForInvoice}
                    onChange={() => handleCheckboxChange("useForInvoice")}
                    className="me-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Use for POS"
                    checked={selectedAccount.useForPOS}
                    onChange={() => handleCheckboxChange("useForPOS")}
                  />
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BankDetailsTable;
